/**
 * Variant picker: hide combinations that were never made.
 *
 * There are two reasons an option value can't be picked, and they mean opposite
 * things to a shopper:
 *
 *   1. No variant exists for the combination. DYMO never made 24mm Black on
 *      Blue. Rendering it struck through reads as "temporarily out of stock" and
 *      invites the shopper to wait for a restock that will never come.
 *   2. The variant exists but is sold out right now. That is real stock
 *      information and it may come back, so it must keep showing.
 *
 * global.js only knows case 2: VariantSelects.updateVariantStatuses marks every
 * non-purchasable value `.disabled`, which paints the diagonal strike in
 * section-main-product.css. This file adds the missing distinction on top,
 * marking case 1 with `.option-missing` (hidden by the rule that
 * snippets/product-variant-options.liquid emits next to the markup).
 *
 * Deliberately additive: a delegated `change` listener on document, registered
 * after the element's own listener and running in the bubble phase, so it always
 * sees the state global.js just produced. Nothing in VariantSelects or
 * VariantRadios is overridden, so `disabled`, `variant:change`, the SKU/UPC/MPN
 * row and the stock block behave exactly as before.
 *
 * The first option is never filtered, matching updateVariantStatuses, which
 * skips index 0. Widths always stay visible, so the shopper can never be walked
 * into a corner where the only route back is the browser's back button.
 *
 * Loaded by snippets/product-variant-options.liquid, once per picker, only where
 * that snippet stamped `data-missing-aware` on the inputs it rendered under this
 * rule. Reading the opt-in off the markup keeps the server render and this
 * client update from ever disagreeing.
 */
(function () {
	"use strict";

	if (window.themeVariantMissingCombinations) return;
	window.themeVariantMissingCombinations = true;

	var PICKER = "variant-radios, variant-selects";
	var FIELDSET = ".product-form__controls";

	function getVariantData(picker) {
		var script = picker.querySelector('script[type="application/json"]');
		if (!script) return null;

		try {
			var data = JSON.parse(script.textContent);
			return Array.isArray(data) ? data : null;
		} catch (error) {
			console.warn("variant-missing-combinations: bad variant JSON", error);
			return null;
		}
	}

	function update(picker) {
		var variants = getVariantData(picker);
		if (!variants) return;

		var fieldsets = Array.prototype.slice.call(
			picker.querySelectorAll(FIELDSET)
		);

		// What is chosen right now, per option, in option order.
		var selected = fieldsets.map(function (fieldset) {
			var checked = fieldset.querySelector('input[type="radio"]:checked');
			return checked ? checked.value : null;
		});

		fieldsets.forEach(function (fieldset, index) {
			// Option 1 is the entry point into the matrix and is never filtered.
			if (index === 0) return;

			var prefix = selected.slice(0, index);
			// Nothing chosen upstream means nothing can be proven; leave it alone.
			if (prefix.indexOf(null) !== -1) return;

			// Values that exist as a variant given everything chosen before this
			// option, in stock or not. Availability is global.js's job, not ours.
			var existing = variants
				.filter(function (variant) {
					return prefix.every(function (value, position) {
						return variant["option" + (position + 1)] === value;
					});
				})
				.map(function (variant) {
					return variant["option" + (index + 1)];
				});

			var inputs = fieldset.querySelectorAll("input[data-missing-aware]");
			Array.prototype.forEach.call(inputs, function (input) {
				// The shopper's own selection is never hidden, even when it has no
				// variant under the new upstream choice. It stays visible and struck
				// through, so the picker always shows what they actually picked.
				var missing = existing.indexOf(input.value) === -1 && !input.checked;
				input.classList.toggle("option-missing", missing);
			});
		});
	}

	document.addEventListener("change", function (event) {
		var target = event.target;
		if (!target || !target.matches || !target.matches("input[type='radio']")) {
			return;
		}

		var picker = target.closest(PICKER);
		if (!picker || !picker.querySelector("input[data-missing-aware]")) return;

		update(picker);
	});
})();
